/**
 * Map Name Resolver — Resolves raw atlas IDs to human-readable names
 * for use in feedback/summary displays.
 *
 * All atlas data is statically imported (no async needed at format time).
 */

import citiesData from '../../../content/atlases/nl/cities.json';
import provincesTopo from '../../../content/atlases/nl/provinces.topo.json';
import { ISLANDS_NAME_BY_ID } from './buildIslandsAtlas';
interface CityRecord {
  id: string;
  name: string;
  lat: number;
  lon: number;
  province?: string;
}
interface RegionGeometry {
  id?: string;
  properties?: {
    name?: string;
  };
}

// ── Build lookup maps once ───────────────────────────────────

const CITY_BY_ID = new Map<string, CityRecord>();
for (const city of citiesData.cities ?? []) {
  CITY_BY_ID.set(city.id, city as CityRecord);
}
const REGION_NAME_BY_ID = new Map<string, string>();
function ingestRegions(topo: unknown) {
  const geoms = (topo as {
    objects?: {
      regions?: {
        geometries?: RegionGeometry[];
      };
    };
  }).objects?.regions?.geometries ?? [];
  for (const g of geoms) {
    if (g.id && g.properties?.name) {
      REGION_NAME_BY_ID.set(g.id, g.properties.name);
    }
  }
}
ingestRegions(provincesTopo);
for (const [id, name] of Object.entries(ISLANDS_NAME_BY_ID)) {
  REGION_NAME_BY_ID.set(id, name);
}

// ── Resolvers ────────────────────────────────────────────────

export function resolveCityName(cityId: string | undefined | null): string {
  if (!cityId) return 'Onbekende stad';
  const city = CITY_BY_ID.get(cityId);
  return city?.name ?? cityId;
}
export function getCityRecord(cityId: string | undefined | null): CityRecord | undefined {
  if (!cityId) return undefined;
  return CITY_BY_ID.get(cityId);
}

/**
 * Resolve any region ID (province or island) to its human-readable name.
 */
export function resolveRegionName(regionId: string | undefined | null): string {
  if (!regionId) return 'Onbekend gebied';
  return REGION_NAME_BY_ID.get(regionId) ?? regionId;
}

/**
 * @deprecated Use resolveRegionName. Kept for backwards compatibility.
 */
export function resolveProvinceName(regionId: string | undefined | null): string {
  if (!regionId) return 'Onbekende provincie';
  return REGION_NAME_BY_ID.get(regionId) ?? regionId;
}

/**
 * Generic map-item name resolver.
 * Tries city first, then region (province/island), then returns the raw ID.
 */
export function resolveMapItemName(id: string | undefined | null): string {
  if (!id) return '';
  if (CITY_BY_ID.has(id)) return CITY_BY_ID.get(id)!.name;
  if (REGION_NAME_BY_ID.has(id)) return REGION_NAME_BY_ID.get(id)!;
  return id;
}

/**
 * Find the nearest known city to a (lat, lon) — used to label
 * a user's clicked point in feedback.
 * Returns null if no city is within `maxKm`.
 */
export function findNearestCity(lat: number, lon: number, maxKm: number = 60): CityRecord | null {
  // Inline haversine to avoid circular imports with geo.ts
  const R = 6371;
  const toRad = (d: number) => d * Math.PI / 180;
  let best: CityRecord | null = null;
  let bestDist = Infinity;
  for (const city of CITY_BY_ID.values()) {
    const dLat = toRad(city.lat - lat);
    const dLon = toRad(city.lon - lon);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat)) * Math.cos(toRad(city.lat)) * Math.sin(dLon / 2) ** 2;
    const dist = 2 * R * Math.asin(Math.sqrt(a));
    if (dist < bestDist && dist <= maxKm) {
      bestDist = dist;
      best = city;
    }
  }
  return best;
}

/**
 * Haversine helper exposed for feedback formatters.
 */
export function distanceKm(a: {
  lat: number;
  lon: number;
}, b: {
  lat: number;
  lon: number;
}): number {
  const R = 6371;
  const toRad = (d: number) => d * Math.PI / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}